import { BodyL, FlowPage } from '@monthio/design-system'
import { Block, Check } from '@monthio/icons'
import {
	COLORS,
	CurrencyDisplay,
	LoadingScreen,
} from '@monthio/react-components'
import { budgetApiClient } from '#api/budgetApiClient'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { ClientInputsType } from '../../../types'
import { AutomatedEvaluationEnum } from '../../../types/consumerBudgetOutputResponseDto'
import { useFlowCompletedModule } from '../useFlowCompletedModule'

const TopWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	flex: 2;
	gap: 30px;
	margin: 15vh 0;
`

const ValuesRow = styled.div`
	background: ${COLORS.GREY_SCALE.WHITE};
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 20px;
	margin: 2px;
	padding: ${(props) => props.theme.spacing(3)}px;

	${(props) => props.theme.breakpoints.up('sm')} {
		padding: ${(props) => props.theme.spacing(6)}px;
	}
`

export const IndicationOfResult = () => {
	const { t } = useTranslation()
	const {
		data: { clientInputs },
	} = useFlowCompletedModule()
	const [result, setResult] = useState<AutomatedEvaluationEnum>()
	const [error, setError] = useState(false)

	useEffect(() => {
		const getConsumerBudgetOutput = async () => {
			const consumerBudgetOutput =
				await budgetApiClient.getConsumerBudgetOutput()

			if (consumerBudgetOutput.insights?.automatedEvaluation?.decision) {
				setResult(consumerBudgetOutput.insights.automatedEvaluation.decision)
			} else {
				setResult(AutomatedEvaluationEnum.NotWithinCreditPolicy)
			}
		}

		getConsumerBudgetOutput().catch((_error) => {
			setError(true)
			console.error('Failed to fetch budget output:', _error)
		})
	}, [])

	if (error) {
		return (
			<FlowPage>
				<FlowPage.Content $isCentered $spacing="xs">
					<FlowPage.Header title={t('indicationOfResult-failure-text')} />
				</FlowPage.Content>
			</FlowPage>
		)
	}

	if (!result) {
		return (
			<FlowPage>
				<FlowPage.Content $isCentered>
					<LoadingScreen title={t('indicationOfResult-loading-text')} />
				</FlowPage.Content>
			</FlowPage>
		)
	}

	const monthlyPayment = () => {
		let _monthlyPayment
		if (clientInputs?.type === ClientInputsType.CarLoan)
			_monthlyPayment = clientInputs.assets?.vehicles[0]?.expenses?.loan
		if (clientInputs?.type === ClientInputsType.CarLeasing)
			_monthlyPayment = clientInputs.assets?.vehicles[0]?.expenses?.leasing
		return _monthlyPayment ? Math.abs(_monthlyPayment) : '-'
	}

	const loanAmount =
		Math.abs(clientInputs?.assets?.vehicles[0]?.financingDetails?.price ?? 0) -
		Math.abs(
			clientInputs?.assets?.vehicles[0]?.financingDetails?.downPayment ?? 0,
		)

	return (
		<FlowPage>
			<FlowPage.Content $isCentered $spacing="xs">
				<TopWrapper>
					{result === AutomatedEvaluationEnum.WithinCreditPolicy ? (
						<Check width={'107px'} />
					) : (
						<Block width={'107px'} />
					)}
					<div>
						<FlowPage.Header
							title={t(`indicationOfResult-${result}-title`)}
							description={t(`indicationOfResult-${result}-description`)}
						/>
					</div>
					<div>
						<ValuesRow>
							<BodyL>{t('indicationOfResult-values-loanAmount')}</BodyL>
							<CurrencyDisplay amount={loanAmount} />
						</ValuesRow>
						<ValuesRow>
							<BodyL>{t('indicationOfResult-values-monthlyPayment')}</BodyL>
							<CurrencyDisplay amount={monthlyPayment()} />
						</ValuesRow>
					</div>
				</TopWrapper>
				{result === AutomatedEvaluationEnum.WithinCreditPolicy ? (
					<BodyL>{t(`indicationOfResult-${result}-note`)}</BodyL>
				) : null}
			</FlowPage.Content>
		</FlowPage>
	)
}
